import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    FormControlLabel,
    Switch,
    TablePagination,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import cuentaCorrienteService, { CuentaCorrienteSaldo } from '../../services/cuentaCorrienteService';
import clienteService from '../../services/clienteService';
import { formatCurrency } from '../../utils/formatters';

interface ClienteSaldoSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (cliente: CuentaCorrienteSaldo) => void;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const DEFAULT_PAGE_SIZE = 20;

const ClienteSaldoSearch: React.FC<ClienteSaldoSearchProps> = ({ open, onClose, onSelect }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [soloConSaldo, setSoloConSaldo] = useState(true);
    const [clientes, setClientes] = useState<CuentaCorrienteSaldo[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [totalCount, setTotalCount] = useState(0);
    const saldosCacheRef = useRef<Map<number, CuentaCorrienteSaldo>>(new Map());

    const handleSearch = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const trimmed = searchTerm.trim();
            const compact = trimmed.replace(/\s+/g, '');
            const searchParam = compact !== '' ? trimmed : undefined;

            if (soloConSaldo) {
                // Solo clientes con saldo - paginado
                const response = await cuentaCorrienteService.getSaldos({
                    pageNumber: page + 1,
                    pageSize: rowsPerPage,
                    searchTerm: searchParam
                });
                const items = response.items || [];
                setClientes(items);
                const count = response.totalCount ?? items.length;
                setTotalCount(count);
                if (count === 0) setError('No se encontraron clientes.');
            } else {
                // Clientes con y sin saldo - paginado
                const [saldosResponse, clientesResponse] = await Promise.all([
                    cuentaCorrienteService.getSaldos({
                        pageNumber: 1,
                        pageSize: 10000,
                        searchTerm: searchParam
                    }),
                    clienteService.getClientes({
                        searchTerm: searchParam,
                        pageNumber: page + 1,
                        pageSize: rowsPerPage
                    })
                ]);

                const saldosMap = new Map(
                    (saldosResponse.items || []).map((p) => [p.nroCliente, p])
                );
                saldosCacheRef.current = saldosMap;

                const result: CuentaCorrienteSaldo[] = (clientesResponse.items || []).map(
                    (p) =>
                        saldosMap.get(p.nroCliente) ?? {
                            nroCliente: p.nroCliente,
                            nombre: p.nombre || '',
                            saldoTotal: 0,
                            saldoVencido: 0,
                            saldoVigente: 0,
                            ultimaActualizacion: ''
                        }
                );

                setClientes(result);
                const count = clientesResponse.totalCount ?? 0;
                setTotalCount(count);
                if (count === 0) setError('No se encontraron clientes.');
            }
        } catch (err: any) {
            setError(`Error al buscar clientes: ${err.message || 'Error desconocido'}`);
            setClientes([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, soloConSaldo, page, rowsPerPage]);

    const handleSelect = (cliente: CuentaCorrienteSaldo) => {
        onSelect(cliente);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setClientes([]);
        setError(null);
        setPage(0);
        setTotalCount(0);
        onClose();
    };

    const handleChangePage = (_: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10) || DEFAULT_PAGE_SIZE);
        setPage(0);
    };

    const handleSwitchChange = (checked: boolean) => {
        setSoloConSaldo(checked);
        setPage(0);
    };

    // Reset página cuando cambia búsqueda o switch
    useEffect(() => {
        if (open) setPage(0);
    }, [open, searchTerm, soloConSaldo]);

    // Búsqueda cuando cambian criterios
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setClientes([]);
            setError(null);
            setPage(0);
            setTotalCount(0);
            return;
        }
        const handle = setTimeout(() => {
            handleSearch();
        }, 400);
        return () => clearTimeout(handle);
    }, [open, searchTerm, soloConSaldo, page, rowsPerPage, handleSearch]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Cliente
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número de cliente o nombre"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                    <FormControlLabel
                        control={
                            <Switch
                                checked={soloConSaldo}
                                onChange={(e) => handleSwitchChange(e.target.checked)}
                                color="primary"
                            />
                        }
                        label="Mostrar solo con saldo"
                    />
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {error && !loading && (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                )}

                {!loading && clientes.length > 0 && (
                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nro. Cliente</TableCell>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell align="right">Saldo Total</TableCell>
                                    <TableCell align="right">Saldo Vigente</TableCell>
                                    <TableCell align="right">Saldo Vencido</TableCell>
                                    <TableCell>Acción</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clientes.map((cliente) => (
                                    <TableRow key={cliente.nroCliente} hover>
                                        <TableCell>{cliente.nroCliente}</TableCell>
                                        <TableCell>{cliente.nombre}</TableCell>
                                        <TableCell align="right">{formatCurrency(cliente.saldoTotal)}</TableCell>
                                        <TableCell align="right">{formatCurrency(cliente.saldoVigente)}</TableCell>
                                        <TableCell align="right">{formatCurrency(cliente.saldoVencido)}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleSelect(cliente)}
                                            >
                                                Seleccionar
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 1
                }}
            >
                <Box sx={{ flexGrow: 1 }}>
                    {totalCount > 0 && (
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                            labelRowsPerPage="Filas por página"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
                            }
                        />
                    )}
                </Box>
                <Button onClick={handleClose} variant="outlined" size="small">
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ClienteSaldoSearch;
